import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePlanLapse } from '@/hooks/usePlanLapse'
import { AnimatePresence } from 'framer-motion';
import { getBadgeClasses, getBadgeDotColor, cn, formatDate } from '../../lib/utils';
import { ContactTrie } from '../../lib/contactTrie';
import {
  UsersThree,
  Plus,
  Upload,
  MagnifyingGlass,
  Funnel,
  X,
  Envelope,
  MapPin,
  LinkedinLogo,
  Sparkle,
  Tag,
  CaretDown,
  CaretLeft,
  CaretRight,
  CheckCircle,
  CursorClick,
  Star,
  Eye,
  DotsThree,
  Lightning,
  ListBullets,
  Trash,
  CalendarBlank,
  Export,
  ArrowsClockwise,
} from '@phosphor-icons/react';
import { useContactStore, Contact, ContactList } from '../../stores/contactStore';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Tooltip } from '../../components/ui/Tooltip';
import DateRangePicker from '../../components/ui/DateRangePicker';
import apolloIcon from '@/assets/apollo-icon.jpeg';
import zohoIcon from '@/assets/providers/zoho-crm.png';
import hubspotIcon from '@/assets/providers/hubspot.png';
import salesforceIcon from '@/assets/providers/salesforce.webp';
import pipedriveIcon from '@/assets/providers/pipedrive.png';
import { toast } from 'sonner';
import { useTrialStatus } from '../../hooks/useTrialStatus';


import { AddContactModal } from './components/AddContactModal';
import { ImportContactsModal } from './components/ImportContactsModal';
import { ApolloSearchModal } from './components/ApolloSearchModal';
import { ZohoImportModal } from './components/ZohoImportModal';
import { ContactDetailModal } from './components/ContactDetailModal';
import { BulkActionsBar } from './components/BulkActionsBar';
import { CrmExportModal } from './components/CrmExportModal';
import { usePermission } from '@/hooks/usePermission';
import { CrmImportModal, type CrmImportProvider } from './components/CrmImportModal';

function Highlight({ text, query }: { text: string | undefined | null; query: string }) {
  if (!text) return null;
  const q = query.trim();
  if (!q) return <>{text}</>;
  const lower = text.toLowerCase();
  const needle = q.toLowerCase();
  const idx = lower.indexOf(needle);
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="rounded-sm px-0.5 bg-yellow-300/70 text-foreground dark:bg-yellow-400/40 dark:text-[#f7f8f8]">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}

// Source rows — shared by the click-menu and the hover preview so both stay
// in sync. `interactive={false}` renders a read-only preview (no click, no
// hover highlight) for the hover tooltip.
// CRMs (besides Zoho) offering browse-and-import via the generic /crm flow.
const CRM_SOURCE_ROWS: {
  provider: CrmImportProvider;
  name: string;
  description: string;
  logo: string;
  /** Logo is a full-bleed colored tile — fill edge-to-edge (like Apollo) instead of white-boxing. */
  fill?: boolean;
}[] = [
  { provider: 'hubspot', name: 'HubSpot', description: 'Import your CRM contacts', logo: hubspotIcon, fill: true },
  { provider: 'salesforce', name: 'Salesforce', description: 'Import your CRM contacts', logo: salesforceIcon },
  { provider: 'pipedrive', name: 'Pipedrive', description: 'Import your people records', logo: pipedriveIcon, fill: true },
];

function SourceMenuItems({
  onApollo,
  onZoho,
  onCrm,
  interactive = true,
  compact = false,
}: {
  onApollo?: () => void;
  onZoho?: () => void;
  onCrm?: (provider: CrmImportProvider) => void;
  interactive?: boolean;
  /** High-level variant: icon + name only, no descriptions, tighter rows. */
  compact?: boolean;
}) {
  const rowBase = cn(
    'w-full flex items-center gap-3 px-4 text-left text-foreground dark:text-[#f7f8f8]',
    compact ? 'py-2' : 'py-2.5',
  );
  const rowInteractive =
    'hover:bg-muted/40 dark:hover:bg-white/[0.04] transition-colors duration-100';
  return (
    <>
      <button
        type="button"
        disabled={!interactive}
        className={cn(rowBase, interactive ? rowInteractive : 'cursor-default')}
        onClick={onApollo}
      >
        <img
          src={apolloIcon}
          alt=""
          className="h-5 w-5 rounded ring-1 ring-border/60 dark:ring-white/[0.08] object-cover flex-shrink-0"
        />
        <div className="text-left min-w-0">
          <p style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>Apollo search</p>
          {!compact && (
            <p
              className="text-muted-foreground dark:text-[#8a8f98]"
              style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
            >
              Search 275M+ contacts
            </p>
          )}
        </div>
      </button>
      <button
        type="button"
        disabled={!interactive}
        className={cn(rowBase, interactive ? rowInteractive : 'cursor-default')}
        onClick={onZoho}
      >
        <img
          src={zohoIcon}
          alt=""
          className="h-5 w-5 rounded ring-1 ring-border/60 dark:ring-white/[0.08] object-contain bg-white p-0.5 flex-shrink-0"
        />
        <div className="text-left min-w-0">
          <p style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>Zoho CRM</p>
          {!compact && (
            <p
              className="text-muted-foreground dark:text-[#8a8f98]"
              style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
            >
              Import your contacts & leads
            </p>
          )}
        </div>
      </button>
      {CRM_SOURCE_ROWS.map(({ provider, name, description, logo, fill }) => (
        <button
          key={provider}
          type="button"
          disabled={!interactive}
          className={cn(rowBase, interactive ? rowInteractive : 'cursor-default')}
          onClick={() => onCrm?.(provider)}
        >
          <img
            src={logo}
            alt=""
            className={cn(
              'h-5 w-5 rounded ring-1 ring-border/60 dark:ring-white/[0.08] flex-shrink-0',
              fill ? 'object-cover' : 'object-contain bg-white p-0.5',
            )}
          />
          <div className="text-left min-w-0">
            <p style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>{name}</p>
            {!compact && (
              <p
                className="text-muted-foreground dark:text-[#8a8f98]"
                style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
              >
                {description}
              </p>
            )}
          </div>
        </button>
      ))}
    </>
  );
}

export default function ContactsPage() {
  const {
    contacts,
    selectedContacts,
    pagination,
    contactStats,
    loading,
    activeTags,
    allTags,
    contactLists,
    fetchContacts,
    fetchContactCount,
    fetchTags,
    fetchContactLists,
    deleteContactList,
    createContactList,
    createTag,
    deleteTag,
    setActiveTags,
    setFilters,
    setPagination,
    selectContact,
    deselectContact,
    selectContacts,
    deselectAllContacts,
    pruneSelection,
  } = useContactStore();
  const { isTrial, limits: trialLimits } = useTrialStatus();

  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showApolloModal, setShowApolloModal] = useState(false);
  const [showZohoModal, setShowZohoModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showSourceMenu, setShowSourceMenu] = useState(false);
  const [showSourcePreview, setShowSourcePreview] = useState(false);
  const [showListsPanel, setShowListsPanel] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [creatingList, setCreatingList] = useState(false);
  const [showTagsPanel, setShowTagsPanel] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [creatingTag, setCreatingTag] = useState(false);
  const [deleteTagName, setDeleteTagName] = useState<string | null>(null);
  const [isDeletingTag, setIsDeletingTag] = useState(false);

  // Filter states
  const [leadStatusFilter, setLeadStatusFilter] = useState<string>('');
  const [emailStatusFilter, setEmailStatusFilter] = useState<string>('');
  // Per-column text filters (Name, Company, Location) for the data grid.
  const [nameFilter, setNameFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  // Last-modified date range filter (YYYY-MM-DD). Applied on "Apply".
  const [modifiedAfter, setModifiedAfter] = useState('');
  const [modifiedBefore, setModifiedBefore] = useState('');
  // Multi-list filter (the Filters dropdown allows several; the standalone
  // Lists dropdown selects one). `activeList` is derived for UI that only makes
  // sense for a single list (hero copy, empty state) — null when 0 or >1 active.
  const [activeLists, setActiveLists] = useState<ContactList[]>([]);
  const activeList = activeLists.length === 1 ? activeLists[0] : null;
  const applyListFilter = (lists: ContactList[]) => {
    setActiveLists(lists);
    setFilters({ list_ids: lists.length ? lists.map((l) => l.id).join(',') : undefined, list_id: undefined });
  };
  const [deleteListId, setDeleteListId] = useState<number | null>(null);
  const [isDeletingList, setIsDeletingList] = useState(false);
  // Analyst holds no bulk_export cell, so these icons render inert for them
  // (the handlers below bail too — the styling is not the guard). A Rep's
  // "gated" scope is allowed: the modal files a request instead.
  const canExportContactsPerm = usePermission('contacts', 'bulk_export');
  const canExportContacts = canExportContactsPerm.allowed;
  // Analyst holds no cell for any of these, so the whole Lists / Tags /
  // Sources / Import / Add-contact toolbar row is hidden for them rather than
  // rendered into a guaranteed 403. Backend guards (require_permission on
  // every one of these routes) remain the real boundary — see
  // backend/app/core/permissions.py.
  const canManageLists = usePermission('lists', 'manage').allowed;
  const canManageTags = usePermission('tags', 'manage').allowed;
  // "Sources" (Apollo / Zoho / HubSpot / Salesforce / Pipedrive) pulls leads
  // into the account — the same contacts.import cell the Import button needs.
  const canImportContacts = usePermission('contacts', 'import').allowed;
  const { lapsed: planLapsed, reason: lapsedTitle } = usePlanLapse()
  const canCreateContacts = usePermission('contacts', 'create_edit').allowed;
  // List queued for CRM export (drives the shared CrmExportModal).
  const [exportListTarget, setExportListTarget] = useState<ContactList | null>(null);
  // Tag queued for CRM export (exports every contact carrying the tag).
  const [exportTagTarget, setExportTagTarget] = useState<{ name: string; count: number } | null>(null);
  // CRM selected for contact import from the Sources menu (HubSpot/Salesforce/Pipedrive).
  const [crmImportProvider, setCrmImportProvider] = useState<CrmImportProvider | null>(null);
  // Contact ids whose tag cell is expanded to reveal all tags (vs. the first 2).
  const [expandedTagRows, setExpandedTagRows] = useState<Set<number>>(new Set());

  const toggleTagRow = (contactId: number) => {
    setExpandedTagRows((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId);
      else next.add(contactId);
      return next;
    });
  };

  useEffect(() => {
    // `filters` lives in the Zustand store, which survives route navigation
    // (only a full page reload resets it) — but the on-screen filter chips
    // above (activeLists, leadStatusFilter, etc.) are local state that always
    // starts fresh on mount. Without this reset, leaving the page with a list
    // (or any other filter) selected and coming back re-fetches under the
    // stale filter with no visible chip to explain why, e.g. "only contacts
    // in that list" with an apparently-empty filter bar (AB#22100).
    setFilters({
      search: undefined,
      name: undefined,
      company: undefined,
      location: undefined,
      lead_status: undefined,
      email_status: undefined,
      tags: undefined,
      segments: undefined,
      list_id: undefined,
      list_ids: undefined,
      updated_after: undefined,
      updated_before: undefined,
    });
    fetchContacts();
    fetchContactCount();
    // Analyst holds no tags/lists cell, so these endpoints 403 for them and
    // the controls they feed are hidden — don't ask.
    if (canManageTags) fetchTags();
    if (canManageLists) fetchContactLists();
    // Selection is scoped to this Contacts view session. Clear it on unmount so
    // navigating away and returning doesn't leave the bulk-actions bar showing a
    // stale selection over a freshly-loading list.
    return () => {
      deselectAllContacts();
    };
  }, []);

  // Engaged/Qualified change from background server events (email opens via the
  // tracking pixel, lead qualification during campaign execution) that never go
  // through the contact store, so the overview tiles go stale while the user
  // stays on the page. Re-fetch the stats whenever the tab/window regains focus
  // so returning to Contacts shows current numbers. fetchContactCount updates
  // silently (no table loading flag), so this causes no skeleton flash.
  useEffect(() => {
    const refreshStats = () => {
      if (document.visibilityState === 'visible') fetchContactCount();
    };
    window.addEventListener('focus', refreshStats);
    document.addEventListener('visibilitychange', refreshStats);
    return () => {
      window.removeEventListener('focus', refreshStats);
      document.removeEventListener('visibilitychange', refreshStats);
    };
  }, [fetchContactCount]);

  // Debounce server-side search so cross-page results stay in sync without
  // refetching on every keystroke. The trie below does the instant local pass.
  useEffect(() => {
    const trimmed = searchQuery.trim();
    const t = setTimeout(() => {
      setFilters({ search: trimmed || undefined });
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Debounce the per-column text filters (Name, Company, Location) the same way
  // as search, so typing doesn't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters({
        name: nameFilter.trim() || undefined,
        company: companyFilter.trim() || undefined,
        location: locationFilter.trim() || undefined,
      });
    }, 300);
    return () => clearTimeout(t);
  }, [nameFilter, companyFilter, locationFilter]);

  const handleSearch = () => {
    setFilters({ search: searchQuery.trim() || undefined });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Index every searchable field as a separate entry per contact so a single
  // query matches across all of them at once (mirrors the server-side search).
  // The trie unions ids per field, so a hit in any one surfaces the contact.
  const trie = useMemo(
    () =>
      ContactTrie.from(
        contacts.flatMap((c) => {
          const name = c.full_name || `${c.first_name || ''} ${c.last_name || ''}`.trim();
          return [
            { id: c.id, text: name },
            { id: c.id, text: c.email || '' },
            { id: c.id, text: c.company_name || '' },
            { id: c.id, text: c.job_title || '' },
            { id: c.id, text: [c.city, c.state, c.country].filter(Boolean).join(' ') },
            { id: c.id, text: c.lead_status || '' },
            { id: c.id, text: c.email_status || '' },
            { id: c.id, text: Array.isArray(c.tags) ? c.tags.join(' ') : '' },
          ];
        }),
      ),
    [contacts],
  );

  const matchedIds = useMemo(() => trie.search(searchQuery.trim()), [trie, searchQuery]);

  const handleSelectContact = (id: number) => {
    if (selectedContacts.includes(id)) {
      deselectContact(id);
    } else {
      selectContact(id);
    }
  };

  // Scoped to what's on screen: the trie search can hide loaded rows, so
  // selecting "all" must never reach past the visible set.
  const handleSelectAll = () => {
    const displayedIds = displayedContacts.map((c) => c.id);
    if (displayedIds.length > 0 && displayedIds.every((id) => selectedContacts.includes(id))) {
      deselectAllContacts();
    } else {
      selectContacts(displayedIds);
    }
  };

  const applyLeadStatus = (value: string) => {
    setLeadStatusFilter(value);
    setFilters({ lead_status: value || undefined });
  };

  const applyEmailStatus = (value: string) => {
    setEmailStatusFilter(value);
    setFilters({ email_status: value || undefined });
  };

  // Apply the last-modified date range and refetch (setFilters triggers the
  // fetch + resets to page 1). Either bound may be left empty (open-ended). The
  // range picker guarantees start <= end, so no ordering check is needed here.
  const applyDateRange = (start: string, end: string) => {
    setModifiedAfter(start);
    setModifiedBefore(end);
    setFilters({
      updated_after: start || undefined,
      updated_before: end || undefined,
    });
  };

  const clearDateFilter = () => {
    setModifiedAfter('');
    setModifiedBefore('');
    setFilters({ updated_after: undefined, updated_before: undefined });
  };

  const clearAllFilters = () => {
    setLeadStatusFilter('');
    setEmailStatusFilter('');
    setNameFilter('');
    setCompanyFilter('');
    setLocationFilter('');
    setModifiedAfter('');
    setModifiedBefore('');
    setActiveTags([]);
    setActiveLists([]);
    setFilters({
      lead_status: undefined,
      email_status: undefined,
      tags: undefined,
      list_id: undefined,
      list_ids: undefined,
      search: undefined,
      name: undefined,
      company: undefined,
      location: undefined,
      updated_after: undefined,
      updated_before: undefined,
    });
    setSearchQuery('');
    setShowFilterMenu(false);
  };

  const handleSelectList = (list: ContactList) => {
    // Standalone Lists dropdown is single-select: pick just this list, or clear
    // if it's already the only active one.
    const isOnly = activeLists.length === 1 && activeLists[0].id === list.id;
    applyListFilter(isOnly ? [] : [list]);
    setShowListsPanel(false);
  };

  const clearListFilter = () => {
    applyListFilter([]);
  };

  const handleDeleteList = (e: React.MouseEvent, listId: number) => {
    e.stopPropagation();
    setDeleteListId(listId);
  };

  const handleExportList = (e: React.MouseEvent, list: ContactList) => {
    e.stopPropagation();
    if (!canExportContacts) return;
    setExportListTarget(list);
    setShowListsPanel(false);
  };

  const handleExportTag = (e: React.MouseEvent, tag: { name: string; count: number }) => {
    e.stopPropagation();
    if (!canExportContacts) return;
    setExportTagTarget(tag);
    setShowTagsPanel(false);
  };

  const handleCreateList = async () => {
    const name = newListName.trim();
    if (!name || creatingList) return;
    setCreatingList(true);
    try {
      await createContactList({ name, contact_ids: [] });
      toast.success(`Created list "${name}"`);
      setNewListName('');
    } catch {
      toast.error('Failed to create list');
    } finally {
      setCreatingList(false);
    }
  };

  const handleCreateTag = async () => {
    const name = newTagName.trim();
    if (!name || creatingTag) return;
    setCreatingTag(true);
    try {
      await createTag(name);
      toast.success(`Created tag "${name}"`);
      setNewTagName('');
    } catch {
      toast.error('Failed to create tag');
    } finally {
      setCreatingTag(false);
    }
  };

  const handleSelectTag = (tag: string) => {
    // Standalone Tags dropdown is single-select: pick just this tag, or clear
    // if it's already the only active one.
    const isOnly = activeTags.length === 1 && activeTags[0] === tag;
    setActiveTags(isOnly ? [] : [tag]);
    setShowTagsPanel(false);
  };

  const confirmDeleteTag = async () => {
    if (deleteTagName === null) return;
    setIsDeletingTag(true);
    try {
      await deleteTag(deleteTagName);
      toast.success(`Deleted tag "${deleteTagName}"`);
      setDeleteTagName(null);
    } catch {
      toast.error('Failed to delete tag');
    } finally {
      setIsDeletingTag(false);
    }
  };

  const confirmDeleteList = async () => {
    if (deleteListId === null) return;
    setIsDeletingList(true);
    try {
      const outcome = await deleteContactList(deleteListId);
      if (outcome && outcome.status === 'pending_approval') {
        // A Representative's delete is a request for a manager to approve;
        // the list stays until they release it.
        toast.success('Sent for approval', { description: outcome.message });
        setDeleteListId(null);
        return;
      }
      if (activeLists.some((l) => l.id === deleteListId)) {
        applyListFilter(activeLists.filter((l) => l.id !== deleteListId));
      }
      setDeleteListId(null);
    } catch {
      // toast handled by store
    } finally {
      setIsDeletingList(false);
    }
  };

  // List filtering is applied server-side (via the list_id filter), so `contacts`
  // already holds only the active list's members for the current page. The trie
  // just narrows the loaded page for instant client-side search highlighting.
  const displayedContacts = matchedIds
    ? contacts.filter(c => matchedIds.has(c.id))
    : contacts;

  const allDisplayedSelected =
    displayedContacts.length > 0 && displayedContacts.every((c) => selectedContacts.includes(c.id));
  const someDisplayedSelected = displayedContacts.some((c) => selectedContacts.includes(c.id));

  // A selection that outlives a filter or page change lets a bulk action hit
  // rows the user can no longer see, so drop anything that just went invisible.
  // Depends on the two inputs to `displayedContacts` rather than the array
  // itself, which is rebuilt on every render.
  useEffect(() => {
    const visibleIds = (matchedIds ? contacts.filter((c) => matchedIds.has(c.id)) : contacts).map((c) => c.id);
    pruneSelection(visibleIds);
  }, [contacts, matchedIds]);

  // `indeterminate` isn't expressible as a prop — set it imperatively so a
  // partial selection reads as partial instead of empty.
  const selectAllRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const el = selectAllRef.current;
    if (el) el.indeterminate = someDisplayedSelected && !allDisplayedSelected;
  }, [someDisplayedSelected, allDisplayedSelected]);

  const activeFilterCount = [leadStatusFilter, emailStatusFilter, nameFilter, companyFilter, locationFilter].filter(Boolean).length
    + activeTags.length
    + activeLists.length
    + (modifiedAfter || modifiedBefore ? 1 : 0);

  return (
    <div>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h1
              className="text-foreground dark:text-[#f7f8f8]"
              style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}
            >
              Contacts
            </h1>
            <p
              className="text-muted-foreground dark:text-[#8a8f98] mt-1"
              style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
            >
              Manage leads, segment by tag or list, and enrich with provider data.
            </p>
          </div>

          <div className="flex items-center gap-2.5 flex-shrink-0">
            {/* A Rep's own CRM exports are "gated": exporting is a request,
                not an action, until an Owner/Admin/Manager approves it
                elsewhere. This list is a Rep's only way to check whether a
                request went through without leaving the page and coming
                back. Nobody else needs it -- everyone else with export
                access exports outright. */}
            {canExportContactsPerm.scope === 'gated' && (
              <Tooltip label="Refresh contacts">
                <Button
                  variant="outline"
                  size="sm"
                  loading={loading}
                  onClick={() => fetchContacts()}
                  icon={<ArrowsClockwise className="h-4 w-4" />}
                  aria-label="Refresh contact list"
                >
                  Refresh
                </Button>
              </Tooltip>
            )}
            {/* Lists button */}
            {canManageLists && (
            <div className="relative">
              <Tooltip label="Group contacts into named lists">
                <Button
                  variant="outline"
                  size="sm"
                  icon={<ListBullets className="h-4 w-4" />}
                  onClick={() => { setShowListsPanel(!showListsPanel); setShowTagsPanel(false); setShowSourceMenu(false); }}
                >
                  Lists
                  {contactLists.length > 0 && (
                    <span
                      className="ml-1.5 h-5 min-w-[1.25rem] px-1 rounded-full bg-muted text-muted-foreground dark:bg-white/[0.06] dark:text-[#d0d6e0] inline-flex items-center justify-center tabular-nums"
                      style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                    >
                      {contactLists.length}
                    </span>
                  )}
                </Button>
              </Tooltip>

              {showListsPanel && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowListsPanel(false)} />
                  <div className="absolute right-0 mt-2 w-80 rounded-lg shadow-lg z-50 overflow-hidden bg-card border border-border/60 dark:bg-[#191a1b] dark:border-white/[0.08] dark:shadow-[0_12px_32px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.02)]">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 bg-muted/20 dark:border-white/[0.06] dark:bg-white/[0.02]">
                      <h3
                        className="text-foreground dark:text-[#f7f8f8]"
                        style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}
                      >
                        Contact lists
                      </h3>
                      <div className="flex items-center gap-3">
                        {activeLists.length > 0 && (
                          <button
                            onClick={clearListFilter}
                            className="inline-flex items-center gap-1 text-[#5e6ad2] hover:text-[#7170ff] dark:text-[#a5b4fc] dark:hover:text-[#c4b5fd] transition-colors duration-100"
                            style={{ fontSize: '11px', fontWeight: 590, letterSpacing: '-0.1px' }}
                          >
                            <X className="h-3 w-3" weight="bold" />
                            Clear filter
                          </button>
                        )}
                        <button
                          onClick={() => setShowListsPanel(false)}
                          className="text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] transition-colors duration-100"
                          aria-label="Close lists"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    {contactLists.length === 0 ? (
                      <div className="px-5 py-8 text-center">
                        <div className="h-10 w-10 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto mb-3">
                          <ListBullets className="h-4 w-4 text-muted-foreground dark:text-[#8a8f98]" />
                        </div>
                        <p
                          className="text-foreground dark:text-[#f7f8f8] mb-1"
                          style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}
                        >
                          No lists yet
                        </p>
                        <p
                          className="text-muted-foreground dark:text-[#8a8f98] leading-relaxed max-w-[15rem] mx-auto"
                          style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                        >
                          Create one below, or select contacts and use{' '}
                          <span className="text-foreground/80 dark:text-[#d0d6e0]" style={{ fontWeight: 510 }}>
                            Save as list
                          </span>.
                        </p>
                      </div>
                    ) : (
                      <div className="max-h-80 overflow-y-auto divide-y divide-border/30 dark:divide-white/[0.04]">
                        {contactLists.map(list => (
                          <button
                            key={list.id}
                            onClick={() => handleSelectList(list)}
                            className={cn(
                              'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors duration-100 group',
                              activeLists.some((l) => l.id === list.id)
                                ? 'bg-[#5e6ad2]/[0.06] dark:bg-[#7170ff]/[0.08]'
                                : 'hover:bg-muted/40 dark:hover:bg-white/[0.04]',
                            )}
                          >
                            <div className="h-8 w-8 rounded-md bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.12] flex items-center justify-center flex-shrink-0">
                              <ListBullets className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p
                                className="text-foreground dark:text-[#f7f8f8] truncate"
                                style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                              >
                                {list.name}
                              </p>
                              <p
                                className="text-muted-foreground dark:text-[#8a8f98]"
                                style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                              >
                                {list.total_contacts} contacts
                                {list.description && ` · ${list.description}`}
                              </p>
                            </div>
                            <span className="flex items-center gap-0.5 flex-shrink-0">
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(e) => handleExportList(e, list)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleExportList(e as any, list); } }}
                                aria-disabled={!canExportContacts}
                                className={cn(
                                  "p-1 rounded text-transparent transition-all duration-100",
                                  canExportContacts
                                    ? "group-hover:text-muted-foreground hover:!text-[#5e6ad2] hover:bg-[#5e6ad2]/10 dark:group-hover:text-[#8a8f98] dark:hover:!text-[#a5b4fc] dark:hover:bg-[#5e6ad2]/[0.12] cursor-pointer"
                                    : "group-hover:text-muted-foreground/40 dark:group-hover:text-[#62666d] cursor-not-allowed",
                                )}
                                aria-label="Export list to CRM"
                                title={canExportContacts ? "Export to CRM" : "Your role cannot export contacts to a CRM"}
                              >
                                <Export className="h-3.5 w-3.5" />
                              </span>
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(e) => handleDeleteList(e, list.id)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleDeleteList(e as any, list.id); } }}
                                className="p-1 rounded text-transparent group-hover:text-muted-foreground hover:!text-red-500 hover:bg-red-500/10 dark:group-hover:text-[#8a8f98] dark:hover:!text-red-400 dark:hover:bg-red-500/[0.08] transition-all duration-100 cursor-pointer"
                                aria-label="Delete list"
                                title="Delete list"
                              >
                                <Trash className="h-3.5 w-3.5" />
                              </span>
                            </span>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Create a new list directly from the panel */}
                    <div className="flex items-center gap-2 px-3 py-2.5 border-t border-border/50 bg-muted/20 dark:border-white/[0.06] dark:bg-white/[0.02]">
                      <Input
                        value={newListName}
                        onChange={(e) => setNewListName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateList(); } }}
                        placeholder="Create a new list…"
                        icon={<Plus className="h-4 w-4" />}
                        iconPosition="left"
                        aria-label="New list name"
                        className="h-9"
                      />
                      <Button
                        variant="default"
                        size="sm"
                        className="h-9 shrink-0"
                        loading={creatingList}
                        disabled={!newListName.trim() || creatingList}
                        onClick={handleCreateList}
                      >
                        Create
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
            )}

            {/* Tags button */}
            {canManageTags && (
            <div className="relative">
              <Tooltip label="Group and filter contacts by tag">
                <Button
                  variant="outline"
                  size="sm"
                  icon={<Tag className="h-4 w-4" />}
                  onClick={() => { setShowTagsPanel(!showTagsPanel); setShowListsPanel(false); setShowSourceMenu(false); }}
                >
                  Tags
                  {allTags.length > 0 && (
                    <span
                      className="ml-1.5 h-5 min-w-[1.25rem] px-1 rounded-full bg-muted text-muted-foreground dark:bg-white/[0.06] dark:text-[#d0d6e0] inline-flex items-center justify-center tabular-nums"
                      style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                    >
                      {allTags.length}
                    </span>
                  )}
                </Button>
              </Tooltip>

              {showTagsPanel && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowTagsPanel(false)} />
                  <div className="absolute right-0 mt-2 w-80 rounded-lg shadow-lg z-50 overflow-hidden bg-card border border-border/60 dark:bg-[#191a1b] dark:border-white/[0.08] dark:shadow-[0_12px_32px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.02)]">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 bg-muted/20 dark:border-white/[0.06] dark:bg-white/[0.02]">
                      <h3
                        className="text-foreground dark:text-[#f7f8f8]"
                        style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}
                      >
                        Tags
                      </h3>
                      <div className="flex items-center gap-3">
                        {activeTags.length > 0 && (
                          <button
                            onClick={() => setActiveTags([])}
                            className="inline-flex items-center gap-1 text-[#5e6ad2] hover:text-[#7170ff] dark:text-[#a5b4fc] dark:hover:text-[#c4b5fd] transition-colors duration-100"
                            style={{ fontSize: '11px', fontWeight: 590, letterSpacing: '-0.1px' }}
                          >
                            <X className="h-3 w-3" weight="bold" />
                            Clear filter
                          </button>
                        )}
                        <button
                          onClick={() => setShowTagsPanel(false)}
                          className="text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] transition-colors duration-100"
                          aria-label="Close tags"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    {allTags.length === 0 ? (
                      <div className="px-5 py-8 text-center">
                        <div className="h-10 w-10 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto mb-3">
                          <Tag className="h-4 w-4 text-muted-foreground dark:text-[#8a8f98]" />
                        </div>
                        <p
                          className="text-foreground dark:text-[#f7f8f8] mb-1"
                          style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}
                        >
                          No tags yet
                        </p>
                        <p
                          className="text-muted-foreground dark:text-[#8a8f98] leading-relaxed max-w-[15rem] mx-auto"
                          style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                        >
                          Create one below, or tag contacts to organize them.
                        </p>
                      </div>
                    ) : (
                      <div className="max-h-80 overflow-y-auto divide-y divide-border/30 dark:divide-white/[0.04]">
                        {allTags.map(tag => (
                          <button
                            key={tag.name}
                            onClick={() => handleSelectTag(tag.name)}
                            className={cn(
                              'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors duration-100 group',
                              activeTags.includes(tag.name)
                                ? 'bg-[#5e6ad2]/[0.06] dark:bg-[#7170ff]/[0.08]'
                                : 'hover:bg-muted/40 dark:hover:bg-white/[0.04]',
                            )}
                          >
                            <div className="h-8 w-8 rounded-md bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.12] flex items-center justify-center flex-shrink-0">
                              <Tag className="h-4 w-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p
                                className="text-foreground dark:text-[#f7f8f8] truncate"
                                style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                              >
                                {tag.name}
                              </p>
                              <p
                                className="text-muted-foreground dark:text-[#8a8f98]"
                                style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                              >
                                {tag.count} contact{tag.count === 1 ? '' : 's'}
                              </p>
                            </div>
                            <span className="flex items-center gap-0.5 flex-shrink-0">
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(e) => handleExportTag(e, tag)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleExportTag(e as any, tag); } }}
                                aria-disabled={!canExportContacts}
                                className={cn(
                                  "p-1 rounded text-transparent transition-all duration-100",
                                  canExportContacts
                                    ? "group-hover:text-muted-foreground hover:!text-[#5e6ad2] hover:bg-[#5e6ad2]/10 dark:group-hover:text-[#8a8f98] dark:hover:!text-[#a5b4fc] dark:hover:bg-[#5e6ad2]/[0.12] cursor-pointer"
                                    : "group-hover:text-muted-foreground/40 dark:group-hover:text-[#62666d] cursor-not-allowed",
                                )}
                                aria-label={`Export tag ${tag.name} to CRM`}
                                title={canExportContacts ? "Export to CRM" : "Your role cannot export contacts to a CRM"}
                              >
                                <Export className="h-3.5 w-3.5" />
                              </span>
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(e) => { e.stopPropagation(); setDeleteTagName(tag.name); }}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setDeleteTagName(tag.name); } }}
                                className="p-1 rounded text-transparent group-hover:text-muted-foreground hover:!text-red-500 hover:bg-red-500/10 dark:group-hover:text-[#8a8f98] dark:hover:!text-red-400 dark:hover:bg-red-500/[0.08] transition-all duration-100 cursor-pointer"
                                aria-label={`Delete tag ${tag.name}`}
                                title="Delete tag"
                              >
                                <Trash className="h-3.5 w-3.5" />
                              </span>
                            </span>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Create a new tag directly from the panel */}
                    <div className="flex items-center gap-2 px-3 py-2.5 border-t border-border/50 bg-muted/20 dark:border-white/[0.06] dark:bg-white/[0.02]">
                      <Input
                        value={newTagName}
                        onChange={(e) => setNewTagName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateTag(); } }}
                        placeholder="Create a new tag…"
                        icon={<Plus className="h-4 w-4" />}
                        iconPosition="left"
                        aria-label="New tag name"
                        className="h-9"
                      />
                      <Button
                        variant="default"
                        size="sm"
                        className="h-9 shrink-0"
                        loading={creatingTag}
                        disabled={!newTagName.trim() || creatingTag}
                        onClick={handleCreateTag}
                      >
                        Create
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
            )}

            {/* Source dropdown — click to pin the menu, hover for a preview */}
            {canImportContacts && (
            <div
              className="relative"
              onMouseEnter={() => setShowSourcePreview(true)}
              onMouseLeave={() => setShowSourcePreview(false)}
            >
              <Tooltip label="Pull leads from Apollo, Zoho & more">
                <Button
                  variant="outline"
                  size="sm"
                  icon={<Sparkle className="h-4 w-4" />}
                  onClick={() => { setShowSourceMenu(!showSourceMenu); setShowListsPanel(false); setShowTagsPanel(false); setShowSourcePreview(false); }}
                  aria-haspopup="menu"
                  aria-expanded={showSourceMenu}
                >
                  Sources
                  <CaretDown className={cn('h-3.5 w-3.5 ml-1 opacity-60 transition-transform duration-150', showSourceMenu && 'rotate-180')} />
                </Button>
              </Tooltip>

              {/* Interactive hover preview — shows what's inside before clicking.
                  Hidden while the click-menu is open. The top-full wrapper carries
                  a pt-2 bridge so moving from the button into the card doesn't
                  cross a dead gap (which would flicker it shut). */}
              {showSourcePreview && !showSourceMenu && (
                <div className="absolute right-0 top-full z-50 pt-2">
                  <div className="w-64 rounded-lg shadow-lg overflow-hidden py-1.5 bg-card border border-border/60 dark:bg-[#191a1b] dark:border-white/[0.08] dark:shadow-[0_12px_32px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.02)]">
                    <SourceMenuItems
                      onApollo={() => { setShowApolloModal(true); setShowSourcePreview(false) }}
                      onZoho={() => { setShowZohoModal(true); setShowSourcePreview(false) }}
                      onCrm={(p) => { setCrmImportProvider(p); setShowSourcePreview(false) }}
                    />
                  </div>
                </div>
              )}

              {showSourceMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => { setShowSourceMenu(false); setShowSourcePreview(false); }} />
                  <div className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg z-50 py-1.5 bg-card border border-border/60 dark:bg-[#191a1b] dark:border-white/[0.08] dark:shadow-[0_12px_32px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.02)]">
                    <SourceMenuItems
                      onApollo={() => { setShowApolloModal(true); setShowSourceMenu(false) }}
                      onZoho={() => { setShowZohoModal(true); setShowSourceMenu(false) }}
                      onCrm={(p) => { setCrmImportProvider(p); setShowSourceMenu(false) }}
                    />
                  </div>
                </>
              )}
            </div>
            )}

            {canImportContacts && (
            <Tooltip label="Import contacts from a CSV, Excel, or TXT file">
              <Button
                variant="outline"
                size="sm"
                icon={<Upload className="h-4 w-4" />}
                onClick={() => setShowImportModal(true)}
                disabled={planLapsed}
                title={lapsedTitle}
              >
                Import
              </Button>
            </Tooltip>
            )}

            {canCreateContacts && (
            <Button
              variant="default"
              size="sm"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => setShowAddModal(true)}
              disabled={planLapsed}
              title={lapsedTitle}
            >
              Add contact
            </Button>
            )}
          </div>
        </div>

        {/* Free-trial cap, proactive — only ever shown to a trial account. */}
        {isTrial && trialLimits && (
          <p
            className="text-muted-foreground dark:text-[#8a8f98]"
            style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
          >
            {pagination.total} of {trialLimits.contacts} contacts used (free trial)
          </p>
        )}

        {/* Hero stats block — title + decorative indigo cloud + floating stat pills */}
        <section
          className={cn(
            'relative overflow-hidden rounded-2xl border border-border/50 p-5 sm:p-6',
            'bg-gradient-to-br from-white via-[#f6f7ff] to-[#eef0ff]',
            'dark:border-white/[0.08] dark:from-[#0f1011] dark:via-[#131421] dark:to-[#1a1b2e]',
          )}
        >
          {/* Decorative constellation / network — abstract audience graph,
              theme-aware via currentColor. Reads as connected nodes around a
              hub, suggesting "your audience" without being literal. */}
          <div
            className="pointer-events-none absolute inset-y-0 right-0 w-2/3 max-w-[560px] text-[#7170ff] dark:text-[#7170ff]"
            aria-hidden="true"
          >
            <svg
              viewBox="0 0 560 240"
              preserveAspectRatio="xMaxYMid slice"
              className="absolute inset-0 h-full w-full opacity-85 dark:opacity-65"
            >
              <defs>
                <radialGradient id="ct-glow-hub" cx="68%" cy="50%" r="55%">
                  <stop offset="0%" stopColor="currentColor" stopOpacity="0.18" />
                  <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
                </radialGradient>
                <radialGradient id="ct-glow-soft" cx="30%" cy="80%" r="45%">
                  <stop offset="0%" stopColor="currentColor" stopOpacity="0.10" />
                  <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
                </radialGradient>
                <pattern id="ct-grid" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
                  <circle cx="1" cy="1" r="0.7" fill="currentColor" fillOpacity="0.22" />
                </pattern>
                <radialGradient id="ct-grid-fade" cx="65%" cy="50%" r="60%">
                  <stop offset="0%" stopColor="white" stopOpacity="1" />
                  <stop offset="100%" stopColor="white" stopOpacity="0" />
                </radialGradient>
                <mask id="ct-grid-mask">
                  <rect width="100%" height="100%" fill="url(#ct-grid-fade)" />
                </mask>
              </defs>

              {/* Layered glows */}
              <rect width="100%" height="100%" fill="url(#ct-glow-hub)" />
              <rect width="100%" height="100%" fill="url(#ct-glow-soft)" />

              {/* Halftone dot grid, radially faded so it whispers off-edge */}
              <rect width="100%" height="100%" fill="url(#ct-grid)" mask="url(#ct-grid-mask)" />

              {/* Concentric orbit rings around hub — suggests reach */}
              <g stroke="currentColor" fill="none">
                <circle cx="380" cy="120" r="42" strokeOpacity="0.22" strokeWidth="1" />
                <circle cx="380" cy="120" r="78" strokeOpacity="0.14" strokeWidth="1" />
                <circle cx="380" cy="120" r="118" strokeOpacity="0.07" strokeWidth="1" />
              </g>

              {/* Connection lines — gentle curves between nodes */}
              <g stroke="currentColor" strokeWidth="1" fill="none" strokeOpacity="0.24">
                <path d="M 320 95 Q 355 78 408 102" />
                <path d="M 360 165 Q 405 152 452 130" />
                <path d="M 340 128 L 412 138" />
                <path d="M 295 142 Q 340 170 390 162" />
                <path d="M 408 102 Q 445 110 470 92" />
                <path d="M 380 120 L 295 142" strokeOpacity="0.16" />
                <path d="M 380 120 L 452 130" strokeOpacity="0.16" />
              </g>

              {/* Nodes — varied sizes, fading outward */}
              <g fill="currentColor">
                <circle cx="380" cy="120" r="6.5" fillOpacity="0.90" />
                <circle cx="320" cy="95"  r="4"   fillOpacity="0.62" />
                <circle cx="408" cy="102" r="3.5" fillOpacity="0.56" />
                <circle cx="452" cy="130" r="3.2" fillOpacity="0.50" />
                <circle cx="390" cy="162" r="3.5" fillOpacity="0.55" />
                <circle cx="340" cy="168" r="2.6" fillOpacity="0.42" />
                <circle cx="295" cy="142" r="3"   fillOpacity="0.50" />
                <circle cx="470" cy="92"  r="2.2" fillOpacity="0.38" />
                <circle cx="475" cy="175" r="2.6" fillOpacity="0.42" />
                <circle cx="250" cy="105" r="2"   fillOpacity="0.30" />
              </g>

              {/* Halo ring on hub node */}
              <circle
                cx="380"
                cy="120"
                r="12"
                fill="none"
                stroke="currentColor"
                strokeOpacity="0.32"
                strokeWidth="1.5"
              />
            </svg>
          </div>

          {/* Title */}
          <div className="relative z-10 max-w-md">
            <h2
              className="text-foreground dark:text-[#f7f8f8]"
              style={{ fontSize: '20px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.3 }}
            >
              Audience overview
            </h2>
            <p
              className="text-muted-foreground dark:text-[#8a8f98] mt-1"
              style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.5 }}
            >
              {activeList
                ? `Snapshot of contacts in "${activeList.name}".`
                : 'Snapshot of leads across your workspace.'}
            </p>
          </div>

          {/* Stat pills — float over the decoration */}
          <div className="relative z-10 mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              // Server-wide aggregates (the /stats endpoint applies the same
              // filters, including list_id) so the tiles describe the whole
              // filtered audience — not just the current page.
              { label: 'Total',     value: contactStats.total,     icon: UsersThree,  color: 'text-[#5e6ad2] dark:text-[#a5b4fc]',         bg: 'bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.12]' },
              { label: 'Active',    value: contactStats.active,    icon: CheckCircle, color: 'text-emerald-600 dark:text-emerald-400',     bg: 'bg-emerald-500/10 dark:bg-emerald-500/[0.10]' },
              { label: 'Engaged',   value: contactStats.engaged,   icon: CursorClick, color: 'text-amber-600 dark:text-amber-400',         bg: 'bg-amber-500/10 dark:bg-amber-500/[0.10]' },
              { label: 'Qualified', value: contactStats.qualified, icon: Star,        color: 'text-muted-foreground dark:text-[#8a8f98]',  bg: 'bg-muted/60 dark:bg-white/[0.04]' },
            ].map((stat) => (
              <div
                key={stat.label}
                className={cn(
                  'rounded-xl border bg-white px-4 py-5 transition-colors duration-150',
                  'border-border/40 shadow-[0_1px_2px_rgba(15,16,17,0.04),0_4px_12px_rgba(15,16,17,0.04)]',
                  'dark:bg-[#191a1b]/85 dark:backdrop-blur-sm dark:border-white/[0.08] dark:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_8px_24px_rgba(0,0,0,0.25)]',
                )}
              >
                <p
                  className="text-foreground dark:text-[#f7f8f8] tabular-nums"
                  style={{ fontSize: '28px', fontWeight: 590, letterSpacing: '-0.5px', lineHeight: 1.05 }}
                >
                  {stat.value}
                </p>
                <div className="mt-2.5 flex items-center gap-2">
                  <div
                    className={cn('h-6 w-6 rounded-md flex items-center justify-center flex-shrink-0', stat.bg)}
                    aria-hidden="true"
                  >
                    <stat.icon className={cn('h-3.5 w-3.5', stat.color)} />
                  </div>
                  <p
                    className="text-muted-foreground dark:text-[#8a8f98]"
                    style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                  >
                    {stat.label}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Search + Filters */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="flex-1 max-w-xl relative">
              <Input
                placeholder="Search by name, email, company, title, location, tag, or status…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                icon={<MagnifyingGlass className="h-4 w-4" />}
                iconPosition="left"
                aria-label="Search contacts"
                className={searchQuery ? 'pr-9' : undefined}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.06] transition-colors duration-100"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <div className="relative flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              className="h-10"
              icon={<Funnel className="h-4 w-4" />}
              onClick={() => setShowFilterMenu(!showFilterMenu)}
            >
              Filters
              {activeFilterCount > 0 && (
                <span
                  className="ml-1.5 h-5 min-w-[1.25rem] px-1 rounded-full bg-[#5e6ad2] text-white inline-flex items-center justify-center tabular-nums"
                  style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                >
                  {activeFilterCount}
                </span>
              )}
            </Button>

            {showFilterMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowFilterMenu(false)} />
                <div className="absolute right-0 mt-2 w-[34rem] max-w-[calc(100vw-2rem)] rounded-lg shadow-lg z-50 flex flex-col max-h-[calc(100vh-9rem)] bg-card border border-border/60 dark:bg-[#191a1b] dark:border-white/[0.08] dark:shadow-[0_12px_32px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.02)]">
                  <div className="flex items-center justify-between shrink-0 px-5 pt-5 pb-4">
                    <h3
                      className="text-foreground dark:text-[#f7f8f8]"
                      style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}
                    >
                      Filters
                    </h3>
                    <button
                      onClick={() => setShowFilterMenu(false)}
                      className="text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] transition-colors duration-100"
                      aria-label="Close filters"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-x-4 gap-y-3.5 overflow-y-auto min-h-0 px-5 pb-5">
                    <div>
                      <label className="block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]">
                        Name
                      </label>
                      <Input
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        placeholder="Filter by name"
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]">
                        Company
                      </label>
                      <Input
                        value={companyFilter}
                        onChange={(e) => setCompanyFilter(e.target.value)}
                        placeholder="Filter by company"
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]">
                        Location
                      </label>
                      <Input
                        value={locationFilter}
                        onChange={(e) => setLocationFilter(e.target.value)}
                        placeholder="City, state, or country"
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]">
                        Lead status
                      </label>
                      <Select
                        value={leadStatusFilter}
                        onChange={(v) => applyLeadStatus(v)}
                        ariaLabel="Lead status"
                        className="w-full block"
                        options={[
                          { value: '',            label: 'All' },
                          { value: 'new',         label: 'New' },
                          { value: 'contacted',   label: 'Contacted' },
                          { value: 'engaged',     label: 'Engaged' },
                          { value: 'qualified',   label: 'Qualified' },
                          { value: 'unqualified', label: 'Unqualified' },
                        ]}
                      />
                    </div>

                    <div>
                      <label className="block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]">
                        Email status
                      </label>
                      <Select
                        value={emailStatusFilter}
                        onChange={(v) => applyEmailStatus(v)}
                        ariaLabel="Email status"
                        className="w-full block"
                        options={[
                          { value: '',             label: 'All' },
                          { value: 'active',       label: 'Active' },
                          { value: 'bounced',      label: 'Bounced' },
                          { value: 'invalid',      label: 'Invalid' },
                          { value: 'unsubscribed', label: 'Unsubscribed' },
                          { value: 'complained',   label: 'Complained' },
                        ]}
                      />
                    </div>

                    {/* Tag filter inside dropdown — multi-select; contacts must
                        carry ALL selected tags (backend AND-combines them). */}
                    {allTags.length > 0 && (
                      <div className="col-span-2">
                        <div className="flex items-center justify-between mb-1.5 gap-2">
                          <label className="text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]">
                            Tag
                          </label>
                          <span
                            className="text-muted-foreground/70 dark:text-[#62666d]"
                            style={{ fontSize: '10px', letterSpacing: '-0.05px' }}
                          >
                            Match all
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                          {allTags.map((tag) => {
                            const active = activeTags.includes(tag.name)
                            return (
                              <button
                                key={tag.name}
                                onClick={() => setActiveTags(
                                  active
                                    ? activeTags.filter((t) => t !== tag.name)
                                    : [...activeTags, tag.name]
                                )}
                                aria-pressed={active}
                                className={cn(
                                  'inline-flex items-center gap-1 px-2 py-0.5 rounded-full transition-colors duration-150 border',
                                  active
                                    ? 'bg-[#5e6ad2]/12 text-[#5e6ad2] border-[#5e6ad2]/30 dark:bg-[#5e6ad2]/[0.18] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/40'
                                    : 'bg-muted/40 text-muted-foreground border-border/50 hover:bg-muted/70 hover:border-border/80 dark:bg-white/[0.04] dark:text-[#8a8f98] dark:border-white/[0.06] dark:hover:bg-white/[0.06] dark:hover:border-white/[0.10]',
                                )}
                                style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                              >
                                {tag.name}
                                <span className="opacity-60 tabular-nums">{tag.count}</span>
                                {active && <X className="h-2.5 w-2.5 ml-0.5 opacity-80" weight="bold" />}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    {/* List filter inside dropdown — multi-select; contacts in
                        ANY selected list (backend unions their members). */}
                    {contactLists.length > 0 && (
                      <div className="col-span-2">
                        <div className="flex items-center justify-between mb-1.5 gap-2">
                          <label className="text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]">
                            Contact list
                          </label>
                          <span
                            className="text-muted-foreground/70 dark:text-[#62666d]"
                            style={{ fontSize: '10px', letterSpacing: '-0.05px' }}
                          >
                            Match any
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                          {contactLists.map((list) => {
                            const active = activeLists.some((l) => l.id === list.id)
                            return (
                              <button
                                key={list.id}
                                onClick={() => applyListFilter(
                                  active
                                    ? activeLists.filter((l) => l.id !== list.id)
                                    : [...activeLists, list]
                                )}
                                aria-pressed={active}
                                className={cn(
                                  'inline-flex items-center gap-1 px-2 py-0.5 rounded-full transition-colors duration-150 border',
                                  active
                                    ? 'bg-[#5e6ad2]/12 text-[#5e6ad2] border-[#5e6ad2]/30 dark:bg-[#5e6ad2]/[0.18] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/40'
                                    : 'bg-muted/40 text-muted-foreground border-border/50 hover:bg-muted/70 hover:border-border/80 dark:bg-white/[0.04] dark:text-[#8a8f98] dark:border-white/[0.06] dark:hover:bg-white/[0.06] dark:hover:border-white/[0.10]',
                                )}
                                style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                              >
                                {list.name}
                                <span className="opacity-60 tabular-nums">{list.total_contacts}</span>
                                {active && <X className="h-2.5 w-2.5 ml-0.5 opacity-80" weight="bold" />}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    {activeFilterCount > 0 && (
                      <div className="pt-2 col-span-2">
                        <Button variant="outline" size="sm" onClick={clearAllFilters} className="w-full">
                          Clear all filters
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
            </div>

            {/* Last-modified date-range filter — sits beside Filters */}
            <DateRangePicker
              startDate={modifiedAfter}
              endDate={modifiedBefore}
              onApply={applyDateRange}
              onClear={clearDateFilter}
            />
          </div>
        </div>

        {/* Active filter chips */}
        {activeFilterCount > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="text-muted-foreground dark:text-[#8a8f98] uppercase"
              style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.08em' }}
            >
              Filtered by
            </span>
            {activeTags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#5e6ad2]/10 text-[#5e6ad2] border border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.12] dark:border-[#5e6ad2]/25 dark:text-[#a5b4fc]"
                style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
              >
                <Tag className="h-3 w-3" />
                {t}
                <button onClick={() => setActiveTags(activeTags.filter((x) => x !== t))} className="ml-0.5 hover:opacity-70 transition-opacity" aria-label={`Clear tag ${t}`}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            {activeLists.map((al) => {
              // Re-read the live list so the count stays fresh after imports/deletes.
              const live = contactLists.find((l) => l.id === al.id) ?? al;
              return (
                <span
                  key={al.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#5e6ad2]/10 text-[#5e6ad2] border border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.12] dark:border-[#5e6ad2]/25 dark:text-[#a5b4fc]"
                  style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                >
                  <ListBullets className="h-3 w-3" />
                  {live.name}
                  <span className="opacity-60 tabular-nums">{live.total_contacts}</span>
                  <button onClick={() => applyListFilter(activeLists.filter((l) => l.id !== al.id))} className="ml-0.5 hover:opacity-70 transition-opacity" aria-label={`Clear list ${live.name}`}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              );
            })}
            {leadStatusFilter && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground border border-border/50 dark:bg-white/[0.04] dark:border-white/[0.06] dark:text-[#8a8f98]"
                style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
              >
                Lead: {leadStatusFilter}
                <button onClick={() => applyLeadStatus('')} className="ml-0.5 hover:text-foreground dark:hover:text-[#f7f8f8] transition-colors" aria-label="Clear lead filter">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {emailStatusFilter && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground border border-border/50 dark:bg-white/[0.04] dark:border-white/[0.06] dark:text-[#8a8f98]"
                style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
              >
                Email: {emailStatusFilter}
                <button onClick={() => applyEmailStatus('')} className="ml-0.5 hover:text-foreground dark:hover:text-[#f7f8f8] transition-colors" aria-label="Clear email filter">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {nameFilter && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground border border-border/50 dark:bg-white/[0.04] dark:border-white/[0.06] dark:text-[#8a8f98]"
                style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
              >
                Name: {nameFilter}
                <button onClick={() => setNameFilter('')} className="ml-0.5 hover:text-foreground dark:hover:text-[#f7f8f8] transition-colors" aria-label="Clear name filter">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {companyFilter && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground border border-border/50 dark:bg-white/[0.04] dark:border-white/[0.06] dark:text-[#8a8f98]"
                style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
              >
                Company: {companyFilter}
                <button onClick={() => setCompanyFilter('')} className="ml-0.5 hover:text-foreground dark:hover:text-[#f7f8f8] transition-colors" aria-label="Clear company filter">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {locationFilter && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground border border-border/50 dark:bg-white/[0.04] dark:border-white/[0.06] dark:text-[#8a8f98]"
                style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
              >
                Location: {locationFilter}
                <button onClick={() => setLocationFilter('')} className="ml-0.5 hover:text-foreground dark:hover:text-[#f7f8f8] transition-colors" aria-label="Clear location filter">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {(modifiedAfter || modifiedBefore) && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground border border-border/50 dark:bg-white/[0.04] dark:border-white/[0.06] dark:text-[#8a8f98]"
                style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
              >
                <CalendarBlank className="h-3 w-3" />
                Modified{' '}
                {modifiedAfter && modifiedBefore
                  ? `${modifiedAfter} → ${modifiedBefore}`
                  : modifiedAfter
                  ? `after ${modifiedAfter}`
                  : `before ${modifiedBefore}`}
                <button onClick={clearDateFilter} className="ml-0.5 hover:text-foreground dark:hover:text-[#f7f8f8] transition-colors" aria-label="Clear last-modified filter">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {activeFilterCount > 1 && (
              <button
                onClick={clearAllFilters}
                className="text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] underline-offset-2 hover:underline transition-colors"
                style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
              >
                Clear all
              </button>
            )}
          </div>
        )}

        {/* Contacts List/Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-muted-foreground/20 border-t-[#5e6ad2] dark:border-white/[0.08] dark:border-t-[#7170ff] mx-auto" />
              <p
                className="text-muted-foreground dark:text-[#8a8f98] mt-3"
                style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
              >
                Loading contacts…
              </p>
            </div>
          </div>
        ) : displayedContacts.length === 0 ? (
          <Card variant="default" className="rounded-lg border-border/50 dark:bg-white/[0.02] dark:border-white/[0.08]">
            <div className="text-center py-14 px-6">
              <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto mb-4">
                <UsersThree className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" />
              </div>
              <h3
                className="text-foreground dark:text-[#f7f8f8] mb-2"
                style={{ fontSize: '20px', fontWeight: 590, lineHeight: 1.33, letterSpacing: '-0.24px' }}
              >
                {activeList ? 'No contacts in this list' : 'No contacts yet'}
              </h3>
              <p
                className="text-muted-foreground dark:text-[#8a8f98] mb-6 leading-relaxed max-w-md mx-auto"
                style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
              >
                {activeList
                  ? 'This list has no matching contacts. Try clearing other filters or the search.'
                  : canCreateContacts || canImportContacts
                    ? 'Add your first contact or import from CSV / Excel to get started.'
                    : 'No contacts have been added to this account yet.'}
              </p>
              {!activeList && (canCreateContacts || canImportContacts) && (
                <div className="flex items-center justify-center gap-2">
                  {canCreateContacts && (
                    <Button
                      variant="default"
                      size="sm"
                      icon={<Plus className="h-4 w-4" />}
                      onClick={() => setShowAddModal(true)}
                      disabled={planLapsed}
                      title={lapsedTitle}
                    >
                      Add contact
                    </Button>
                  )}
                  {canImportContacts && (
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<Upload className="h-4 w-4" />}
                      onClick={() => setShowImportModal(true)}
                      disabled={planLapsed}
                      title={lapsedTitle}
                    >
                      Import contacts
                    </Button>
                  )}
                </div>
              )}
            </div>
          </Card>
        ) : (
          <div className="rounded-lg border border-border/50 overflow-hidden bg-card dark:bg-white/[0.02] dark:border-white/[0.08]">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border/50 dark:border-white/[0.06]">
                    <th className="px-3 py-3.5 text-left w-10 align-middle">
                      <div className="flex items-center">
                        <input
                          ref={selectAllRef}
                          type="checkbox"
                          checked={allDisplayedSelected}
                          onChange={handleSelectAll}
                          className="rounded-sm border-border/60 h-4 w-4 accent-[#5e6ad2] block align-middle"
                          aria-label={
                            allDisplayedSelected
                              ? `Deselect all ${displayedContacts.length} contacts on this page`
                              : `Select all ${displayedContacts.length} contacts on this page`
                          }
                        />
                      </div>
                    </th>
                    {(['Contact','Company','Location','Status','Tags','Engagement','Last Modified'] as const).map((h, i) => (
                      <th
                        key={h}
                        className={cn(
                          'px-3 py-3.5 text-left align-middle text-muted-foreground dark:text-[#8a8f98] uppercase',
                          i === 2 && 'hidden lg:table-cell',
                          i === 4 && 'hidden xl:table-cell',
                          i === 5 && 'hidden md:table-cell',
                          i === 6 && 'hidden lg:table-cell',
                        )}
                        style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.08em' }}
                      >
                        {h}
                      </th>
                    ))}
                    <th className="px-3 py-3.5 text-right w-16 align-middle" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50 dark:divide-white/[0.06]">
                  {displayedContacts.map((contact) => {
                    const initials = contact.full_name
                      ? contact.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                      : contact.email[0].toUpperCase();
                    const isSelected = selectedContacts.includes(contact.id);

                    return (
                      <tr
                        key={contact.id}
                        className={cn(
                          'group transition-colors duration-150 cursor-pointer',
                          isSelected
                            ? 'bg-[#5e6ad2]/[0.05] dark:bg-[#7170ff]/[0.06]'
                            : 'hover:bg-muted/40 dark:hover:bg-white/[0.03]',
                        )}
                        onClick={() => setSelectedContact(contact)}
                      >
                        <td className="px-3 py-3.5" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSelectContact(contact.id)}
                            className="rounded-sm border-border/60 h-4 w-4 accent-[#5e6ad2]"
                            aria-label={`Select ${contact.full_name || contact.email}`}
                          />
                        </td>
                        <td className="px-3 py-3.5">
                          <div className="flex items-center gap-3">
                            <div className="relative flex-shrink-0">
                              <div className="h-9 w-9 rounded-full bg-[#5e6ad2]/10 border border-[#5e6ad2]/15 dark:bg-[#5e6ad2]/[0.12] dark:border-[#5e6ad2]/20 flex items-center justify-center">
                                <span
                                  className="text-[#5e6ad2] dark:text-[#a5b4fc] tabular-nums"
                                  style={{ fontSize: '11px', fontWeight: 590, letterSpacing: '-0.1px' }}
                                >
                                  {initials}
                                </span>
                              </div>
                              {contact.enrichment_status === 'completed' && (
                                <div
                                  className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-amber-500 ring-2 ring-card dark:ring-[#0f1011] flex items-center justify-center"
                                  title="Enriched"
                                >
                                  <Lightning className="h-2 w-2 text-white" />
                                </div>
                              )}
                              {contact.enrichment_status === 'in_progress' && (
                                <div
                                  className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-[#5e6ad2] ring-2 ring-card dark:ring-[#0f1011] flex items-center justify-center animate-pulse"
                                  title="Enriching…"
                                >
                                  <Lightning className="h-2 w-2 text-white" />
                                </div>
                              )}
                            </div>
                            <div className="min-w-0">
                              <div
                                className="text-foreground dark:text-[#f7f8f8] truncate"
                                style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                              >
                                {contact.full_name ? (
                                  <Highlight text={contact.full_name} query={searchQuery} />
                                ) : (
                                  <Highlight text={contact.email} query={searchQuery} />
                                )}
                              </div>
                              {contact.full_name && (
                                <div
                                  className="text-muted-foreground dark:text-[#8a8f98] truncate"
                                  style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                                >
                                  <Highlight text={contact.email} query={searchQuery} />
                                </div>
                              )}
                              {contact.job_title && (
                                <div
                                  className="text-muted-foreground/80 dark:text-[#62666d] truncate"
                                  style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                                >
                                  {contact.job_title}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3.5">
                          {contact.company_name ? (
                            <div className="min-w-0">
                              <div
                                className="text-foreground dark:text-[#f7f8f8] truncate"
                                style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                              >
                                <Highlight text={contact.company_name} query={searchQuery} />
                              </div>
                              {contact.company_industry && (
                                <div
                                  className="text-muted-foreground dark:text-[#8a8f98] truncate"
                                  style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                                >
                                  {contact.company_industry}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground/40 dark:text-[#3e3e44]" style={{ fontSize: '12px' }}>—</span>
                          )}
                        </td>
                        <td className="px-3 py-3.5 hidden lg:table-cell">
                          {(contact.city || contact.country) ? (
                            <div
                              className="flex items-center gap-1.5 text-muted-foreground dark:text-[#8a8f98]"
                              style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                            >
                              <MapPin className="h-3 w-3 flex-shrink-0 text-muted-foreground/40 dark:text-[#62666d]" />
                              <span className="truncate">
                                {[contact.city, contact.state, contact.country].filter(Boolean).join(', ')}
                              </span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground/40 dark:text-[#3e3e44]" style={{ fontSize: '12px' }}>—</span>
                          )}
                        </td>
                        <td className="px-3 py-3.5">
                          <div className="flex flex-row flex-wrap gap-1.5">
                            <span
                              className={cn(
                                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full w-fit',
                                getBadgeClasses(contact.lead_status),
                              )}
                              style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                            >
                              <span className={cn('h-1.5 w-1.5 rounded-full', getBadgeDotColor(contact.lead_status))} />
                              {contact.lead_status}
                            </span>
                            <span
                              className={cn(
                                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full w-fit',
                                getBadgeClasses(contact.email_status),
                              )}
                              style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                            >
                              <span className={cn('h-1.5 w-1.5 rounded-full', getBadgeDotColor(contact.email_status))} />
                              {contact.email_status}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-3.5 hidden xl:table-cell">
                          <div className="flex flex-wrap gap-1 max-w-[160px]">
                            {contact.tags && contact.tags.length > 0 ? (
                              <>
                                {(expandedTagRows.has(contact.id) ? contact.tags : contact.tags.slice(0, 2)).map((tag) => (
                                  <span
                                    key={tag}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActiveTags([tag]);
                                    }}
                                    className={cn(
                                      'inline-flex items-center px-2 py-0.5 rounded-full border cursor-pointer transition-colors duration-100 whitespace-nowrap max-w-full truncate',
                                      (tag.toLowerCase() === 'invalid email' || tag.toLowerCase() === 'fake_email')
                                        ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100 dark:bg-red-950/30 dark:text-red-400 dark:border-red-900/40'
                                        : 'bg-muted/60 text-muted-foreground border-border/50 hover:bg-muted/80 hover:text-foreground dark:bg-white/[0.04] dark:text-[#8a8f98] dark:border-white/[0.06] dark:hover:bg-white/[0.06] dark:hover:text-[#f7f8f8]'
                                    )}
                                    style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                                  >
                                    {tag}
                                  </span>
                                ))}
                                {contact.tags.length > 2 && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleTagRow(contact.id);
                                    }}
                                    aria-expanded={expandedTagRows.has(contact.id)}
                                    aria-label={expandedTagRows.has(contact.id) ? 'Show fewer tags' : `Show all ${contact.tags.length} tags`}
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-muted-foreground bg-muted/60 cursor-pointer hover:bg-muted/80 hover:text-foreground transition-colors duration-100 dark:bg-white/[0.04] dark:text-[#8a8f98] dark:hover:bg-white/[0.06] dark:hover:text-[#f7f8f8]"
                                    style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                                  >
                                    {expandedTagRows.has(contact.id) ? 'Show less' : `+${contact.tags.length - 2}`}
                                  </button>
                                )}
                              </>
                            ) : (
                              <span className="text-muted-foreground/40 dark:text-[#3e3e44]" style={{ fontSize: '12px' }}>—</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3.5 hidden md:table-cell">
                          <div
                            className="flex items-center gap-3 tabular-nums"
                            style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                          >
                            <div className="flex items-center gap-1 text-muted-foreground dark:text-[#8a8f98]" title="Emails sent">
                              <Envelope className="h-3 w-3" />
                              <span>{contact.total_emails_sent}</span>
                            </div>
                            <div className="flex items-center gap-1 text-muted-foreground dark:text-[#8a8f98]" title="Opened">
                              <Eye className="h-3 w-3" />
                              <span>{contact.total_emails_opened}</span>
                            </div>
                            <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400" title="Clicked">
                              <CursorClick className="h-3 w-3" />
                              <span>{contact.total_emails_clicked}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3.5 hidden lg:table-cell">
                          {(() => {
                            const modified = contact.updated_at || contact.created_at;
                            return modified ? (
                              <span
                                className="text-muted-foreground dark:text-[#8a8f98] whitespace-nowrap"
                                style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                                title={new Date(modified).toLocaleString()}
                              >
                                {formatDate(modified, 'PP')}
                              </span>
                            ) : (
                              <span className="text-muted-foreground/40 dark:text-[#3e3e44]" style={{ fontSize: '12px' }}>—</span>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-0.5">
                            {contact.linkedin_url && (
                              <a
                                href={contact.linkedin_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 rounded-md text-muted-foreground hover:text-[#0A66C2] hover:bg-[#0A66C2]/10 transition-colors duration-100 dark:text-[#8a8f98] dark:hover:text-[#5e9ad8] dark:hover:bg-[#0A66C2]/[0.12]"
                                aria-label="Open LinkedIn"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <LinkedinLogo weight="fill" className="h-3.5 w-3.5" />
                              </a>
                            )}
                            <button
                              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors duration-100 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05]"
                              onClick={() => setSelectedContact(contact)}
                              aria-label="Contact details"
                            >
                              <DotsThree className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination — server-side, and now list-aware (the list_id filter
            scopes the same paginated query), so it applies whether or not a
            list is selected. */}
        {contacts.length > 0 && (() => {
          const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;
          const totalPages = Math.ceil(pagination.total / pagination.limit);
          const startItem = pagination.skip + 1;
          const endItem = Math.min(pagination.skip + pagination.limit, pagination.total);

          const getPageNumbers = () => {
            const pages: (number | 'ellipsis')[] = [];
            if (totalPages <= 7) {
              for (let i = 1; i <= totalPages; i++) pages.push(i);
            } else {
              pages.push(1);
              if (currentPage > 3) pages.push('ellipsis');
              for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                pages.push(i);
              }
              if (currentPage < totalPages - 2) pages.push('ellipsis');
              pages.push(totalPages);
            }
            return pages;
          };

          return (
            <div className="flex items-center justify-between py-2">
              <p
                className="text-muted-foreground dark:text-[#8a8f98]"
                style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
              >
                {/* The trie narrows the loaded page client-side, so the server's
                    skip/limit range no longer describes what's rendered — print
                    the visible count instead of a range that would overstate it. */}
                {matchedIds ? (
                  <>
                    Showing{' '}
                    <span className="text-foreground dark:text-[#f7f8f8] font-medium tabular-nums">{displayedContacts.length}</span> of{' '}
                    <span className="text-foreground dark:text-[#f7f8f8] font-medium tabular-nums">{pagination.total}</span> contacts
                  </>
                ) : (
                  <>
                    <span className="text-foreground dark:text-[#f7f8f8] font-medium tabular-nums">{startItem}-{endItem}</span> of{' '}
                    <span className="text-foreground dark:text-[#f7f8f8] font-medium tabular-nums">{pagination.total}</span> contacts
                  </>
                )}
              </p>
              <div className="flex items-center gap-1">
                <button
                  disabled={pagination.skip === 0}
                  onClick={() => setPagination({ skip: Math.max(0, pagination.skip - pagination.limit) })}
                  className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-100 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05]"
                  aria-label="Previous page"
                >
                  <CaretLeft className="h-4 w-4" />
                </button>
                {getPageNumbers().map((page, idx) =>
                  page === 'ellipsis' ? (
                    <span key={`ellipsis-${idx}`} className="px-1.5 text-muted-foreground dark:text-[#62666d]" style={{ fontSize: '12px' }}>…</span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => setPagination({ skip: (page - 1) * pagination.limit })}
                      className={cn(
                        'h-7 min-w-[1.75rem] px-2 rounded-md transition-colors duration-100 tabular-nums',
                        page === currentPage
                          ? 'bg-[#5e6ad2] text-white shadow-sm dark:bg-[#5e6ad2]'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05]',
                      )}
                      style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                    >
                      {page}
                    </button>
                  ),
                )}
                <button
                  disabled={pagination.skip + pagination.limit >= pagination.total}
                  onClick={() => setPagination({ skip: pagination.skip + pagination.limit })}
                  className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-100 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05]"
                  aria-label="Next page"
                >
                  <CaretRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })()}

        {/* Bulk Actions Bar — rendered last in the content column on purpose:
            it is `sticky bottom-4`, which only pins a bar to the bottom of the
            viewport while its natural position is still below the fold. Placed
            above the table (where it used to live) the same class is a no-op and
            the bar scrolls away with the toolbar. The modals below it are all
            fixed/portalled, so they add no flow height after it. */}
        <AnimatePresence>
          {selectedContacts.length > 0 && <BulkActionsBar />}
        </AnimatePresence>

        {/* Modals */}
        <AddContactModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} />
        <ImportContactsModal isOpen={showImportModal} onClose={() => setShowImportModal(false)} />
        <ApolloSearchModal isOpen={showApolloModal} onClose={() => setShowApolloModal(false)} />
        <ZohoImportModal isOpen={showZohoModal} onClose={() => setShowZohoModal(false)} />
        <CrmImportModal
          isOpen={crmImportProvider !== null}
          onClose={() => setCrmImportProvider(null)}
          provider={crmImportProvider}
        />
        <CrmExportModal
          isOpen={exportListTarget !== null}
          onClose={() => setExportListTarget(null)}
          listId={exportListTarget?.id}
          contactCount={exportListTarget?.total_contacts}
          sourceName={exportListTarget?.name}
        />
        <CrmExportModal
          isOpen={exportTagTarget !== null}
          onClose={() => setExportTagTarget(null)}
          tag={exportTagTarget?.name}
          contactCount={exportTagTarget?.count}
          sourceName={exportTagTarget ? `${exportTagTarget.name} tag` : undefined}
        />
        {selectedContact && (
          <ContactDetailModal
            // Pull the live copy from the store so edits (updateContact) reflect
            // immediately — the local `selectedContact` is only a snapshot from
            // when the row was clicked and would otherwise stay stale until refresh.
            contact={contacts.find((c) => c.id === selectedContact.id) || selectedContact}
            isOpen={!!selectedContact}
            onClose={() => setSelectedContact(null)}
          />
        )}

        <ConfirmDialog
          open={deleteListId !== null}
          title="Delete list"
          description="Are you sure you want to delete this list? Contacts will not be deleted."
          confirmLabel="Delete"
          cancelLabel="Cancel"
          tone="destructive"
          loading={isDeletingList}
          onConfirm={confirmDeleteList}
          onCancel={() => !isDeletingList && setDeleteListId(null)}
        />

        <ConfirmDialog
          open={deleteTagName !== null}
          title="Delete tag"
          description={`Delete the tag "${deleteTagName ?? ''}"? It will be removed from every contact that carries it. Contacts themselves are not deleted.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          tone="destructive"
          loading={isDeletingTag}
          onConfirm={confirmDeleteTag}
          onCancel={() => !isDeletingTag && setDeleteTagName(null)}
        />
      </div>
    </div>
  );
}
